import { defineStore } from "pinia";

import { getCompanyId, getUserId } from "@/utils/userUtils";

import commentsApi from "@/services/api/commentsApi";
import fleetVehicleRemarksApi from "@/services/api/fleetVehicleRemarksApi";

import { useNotificationStore } from "@/stores/notificationStore";

export const useFleetVehicleRemarkStore = defineStore("fleetVehicleRemark", {
  state: () => ({
    companyId: getCompanyId(),
    remarks: [],
    inspectionActiveRemarks: [],
    inspectionActiveRemarksVehicleId: null,
    inspectionActiveRemarksRequest: 0,
    selectedId: null,
    selectedRecord: null,
    selectionRequest: 0,
    listRequest: 0,
    countsRequest: 0,
    counts: { total: null, active: null, blocking: null, resolved: null, cancelled: null },
    severity: "",
    search: "",
    remarkType: "",
    status: "",
    scope: "active",
    dataIsLoading: false,
    isSubmitting: false,
    pagination: {
      page: 1,
      pages: 1,
      series: [],
      next: null,
      prev: null,
      count: 0,
      limit: 40,
    },
    lastVehicleId: null,
    lastParams: { "q[archived]": "active" },
    currentUserId: getUserId(),
  }),

  getters: {
    selectedRemark: (state) => state.remarks.find((remark) => String(remark.id) === String(state.selectedId)) || state.selectedRecord,
    activePreset: (state) => ["resolved", "cancelled"].includes(state.status) ? state.status : (state.scope === "active" ? (state.severity === "blocking" && state.remarkType !== "information" ? "blocking" : "active") : ""),
  },

  actions: {
    setVehicle(vehicleId) {
      if (String(this.lastVehicleId) === String(vehicleId)) return;
      this.listRequest += 1;
      this.selectionRequest += 1;
      this.lastVehicleId = vehicleId;
      this.remarks = [];
      this.selectedId = null;
      this.selectedRecord = null;
      this.counts = { total: null, active: null, blocking: null, resolved: null, cancelled: null };
    },
    async fetchCounts(vehicleId) {
      const request = ++this.countsRequest;
      const groups = { total: null, active: ["open", "in_progress"], resolved: "resolved", cancelled: "cancelled", blocking: ["open", "in_progress"] };
      const entries = await Promise.all(Object.entries(groups).map(async ([key, status]) => {
        const params = { "q[archived]": "active", page: 1, limit: 1 };
        if (status) params["q[status]"] = status;
        if (key === "blocking") {
          params["q[severity]"] = "blocking";
          params["q[remark_type]"] = "defect";
        }
        const response = await fleetVehicleRemarksApi.index(this.companyId, vehicleId, params);
        return [key, response.data.pagination_meta?.count ?? null];
      }));
      if (request === this.countsRequest && String(vehicleId) === String(this.lastVehicleId)) this.counts = Object.fromEntries(entries);
    },
    async selectRemark(vehicleId, id) {
      const request = ++this.selectionRequest;
      this.selectedId = id;
      this.selectedRecord = null;
      if (id == null || this.remarks.some((remark) => String(remark.id) === String(id))) return;
      const response = await fleetVehicleRemarksApi.show(this.companyId, vehicleId, id);
      if (request === this.selectionRequest && String(vehicleId) === String(this.lastVehicleId)) {
        this.selectedRecord = this.normalizeResponseData(response);
      }
    },
    setScope(scope) { this.scope = scope || ""; this.status = ""; },
    setStatus(status) {
      this.status = status || "";
      if (this.status) this.scope = ["open", "in_progress"].includes(this.status) ? "active" : "";
    },
    selectTab(preset) {
      const finished = ["resolved", "cancelled"].includes(preset);
      this.scope = finished ? "" : "active";
      this.status = finished ? preset : "";
      this.severity = preset === "blocking" ? "blocking" : "";
      this.remarkType = preset === "blocking" ? "defect" : "";
    },

    normalizeResponseData(response) {
      return response.data.result || response.data || [];
    },

    normalizePagination(response) {
      const meta = response.data.pagination_meta || {};

      return {
        page: meta.page || 1,
        pages: meta.pages || 1,
        series: meta.series || [],
        next: meta.next || null,
        prev: meta.prev || null,
        count: meta.count || 0,
        limit: meta.limit || 40,
      };
    },

    async fetchRemarks(vehicleId, params = {}) {
      const request = ++this.listRequest;
      this.dataIsLoading = true;
      this.lastVehicleId = vehicleId;
      this.lastParams = this.remarkParams(params);

      try {
        const response = await fleetVehicleRemarksApi.index(
          this.companyId,
          vehicleId,
          this.lastParams
        );
        if (request !== this.listRequest) return;
        this.remarks = this.normalizeResponseData(response);
        this.pagination = this.normalizePagination(response);
      } finally {
        if (request === this.listRequest) this.dataIsLoading = false;
      }
    },

    async fetchInspectionActiveRemarks(vehicleId) {
      const request = ++this.inspectionActiveRemarksRequest;
      if (String(vehicleId) !== String(this.inspectionActiveRemarksVehicleId)) {
        this.inspectionActiveRemarks = [];
      }
      this.inspectionActiveRemarksVehicleId = vehicleId;
      const response = await fleetVehicleRemarksApi.index(this.companyId, vehicleId, {
        "q[archived]": "active",
        "q[status]": ["open", "in_progress"],
        page: 1,
        limit: 100,
      });
      if (
        request !== this.inspectionActiveRemarksRequest ||
        String(vehicleId) !== String(this.inspectionActiveRemarksVehicleId)
      ) return;

      this.inspectionActiveRemarks = this.normalizeResponseData(response);
      return this.inspectionActiveRemarks;
    },

    async reloadRemarks(vehicleId = this.lastVehicleId) {
      if (!vehicleId) return;

      await this.fetchRemarks(vehicleId, this.lastParams);
    },

    async fetchRemark(vehicleId, id) {
      const response = await fleetVehicleRemarksApi.show(
        this.companyId,
        vehicleId,
        id
      );
      const remark = this.normalizeResponseData(response);

      this.upsertRemark(remark);
      return remark;
    },

    async createRemark(vehicleId, payload) {
      this.isSubmitting = true;

      try {
        const response = await fleetVehicleRemarksApi.create(
          this.companyId,
          vehicleId,
          payload
        );
        await this.reloadRemarks(vehicleId);
        useNotificationStore().showSuccess("Пометка создана");
        return this.normalizeResponseData(response);
      } finally {
        this.isSubmitting = false;
      }
    },

    async createInspectionRemark(vehicleId, inspectionId, payload) {
      this.isSubmitting = true;

      try {
        const response = await fleetVehicleRemarksApi.create(
          this.companyId,
          vehicleId,
          { ...payload, inspection_id: inspectionId }
        );
        return this.normalizeResponseData(response);
      } finally {
        this.isSubmitting = false;
      }
    },

    async updateInspectionRemark(vehicleId, id, payload) {
      this.isSubmitting = true;

      try {
        const response = await fleetVehicleRemarksApi.update(
          this.companyId,
          vehicleId,
          id,
          payload
        );
        const remark = this.normalizeResponseData(response);
        const index = this.inspectionActiveRemarks.findIndex(
          (item) => String(item.id) === String(remark.id)
        );
        if (index !== -1) this.inspectionActiveRemarks.splice(index, 1, remark);
        return remark;
      } finally {
        this.isSubmitting = false;
      }
    },

    async updateRemark(vehicleId, id, payload) {
      this.isSubmitting = true;

      try {
        const response = await fleetVehicleRemarksApi.update(
          this.companyId,
          vehicleId,
          id,
          payload
        );
        const updatedRemark = this.normalizeResponseData(response);

        this.upsertRemark(updatedRemark, false);
        useNotificationStore().showSuccess("Пометка обновлена");
        return updatedRemark;
      } finally {
        this.isSubmitting = false;
      }
    },

    resolveRemark(vehicleId, id, payload) {
      return this.saveStatusTransition(() => fleetVehicleRemarksApi.changeStatus(
        this.companyId,
        vehicleId,
        id,
        { ...payload, status: "resolved" }
      ));
    },
    startWorkRemark(vehicleId, id) {
      return this.saveStatusTransition(() => fleetVehicleRemarksApi.changeStatus(
        this.companyId,
        vehicleId,
        id,
        { status: "in_progress" }
      ));
    },
    cancelRemark(vehicleId, id, payload) {
      return this.saveStatusTransition(() => fleetVehicleRemarksApi.changeStatus(
        this.companyId,
        vehicleId,
        id,
        { ...payload, status: "cancelled" }
      ));
    },
    reopenRemark(vehicleId, id, payload) {
      return this.saveStatusTransition(() => fleetVehicleRemarksApi.changeStatus(
        this.companyId,
        vehicleId,
        id,
        { ...payload, status: "open" }
      ));
    },
    async saveStatusTransition(request) {
      this.isSubmitting = true;
      try {
        const response = await request();
        const remark = this.normalizeResponseData(response);
        this.upsertRemark(remark, false);
        useNotificationStore().showSuccess("Статус пометки изменён");
        return remark;
      } finally {
        this.isSubmitting = false;
      }
    },

    async archiveRemark(vehicleId, id) {
      this.isSubmitting = true;

      try {
        const response = await fleetVehicleRemarksApi.archive(
          this.companyId,
          vehicleId,
          id
        );
        const archivedRemark = this.normalizeResponseData(response);

        await this.reloadRemarks(vehicleId);
        this.upsertRemark(archivedRemark);
        useNotificationStore().showSuccess("Пометка отправлена в архив");
        return archivedRemark;
      } finally {
        this.isSubmitting = false;
      }
    },

    async restoreRemark(vehicleId, id) {
      this.isSubmitting = true;

      try {
        const response = await fleetVehicleRemarksApi.restore(
          this.companyId,
          vehicleId,
          id
        );
        const restoredRemark = this.normalizeResponseData(response);

        await this.reloadRemarks(vehicleId);
        this.upsertVisibleRemark(restoredRemark);
        useNotificationStore().showSuccess("Пометка восстановлена");
        return restoredRemark;
      } finally {
        this.isSubmitting = false;
      }
    },

    async createComment(remarkId, payload) {
      this.isSubmitting = true;

      try {
        const response = await commentsApi.create(this.companyId, {
          ...payload,
          commentable_type: "Fleet::Vehicle::Remark",
          commentable_id: remarkId,
        });
        const comment = this.normalizeResponseData(response);

        this.addCommentToRemark(comment);
        useNotificationStore().showSuccess("Комментарий добавлен");
        return comment;
      } finally {
        this.isSubmitting = false;
      }
    },

    async destroyComment(id) {
      this.isSubmitting = true;

      try {
        const response = await commentsApi.destroy(this.companyId, id);
        const comment = this.normalizeResponseData(response);

        this.removeCommentFromRemark(comment);
        useNotificationStore().showSuccess("Комментарий удалён");
        return comment;
      } finally {
        this.isSubmitting = false;
      }
    },

    upsertRemark(remark, insert = true) {
      if (!remark?.id) return;

      if (String(this.selectedId) === String(remark.id)) this.selectedRecord = remark;
      const index = this.remarks.findIndex((item) => item.id === remark.id);
      if (index === -1) {
        if (!insert) return;
        this.remarks.unshift(remark);
        return;
      }

      this.remarks.splice(index, 1, remark);
    },

    upsertVisibleRemark(remark) {
      if (
        this.lastParams["q[archived]"] === "only" &&
        !remark.archived_at
      ) {
        return;
      }

      this.upsertRemark(remark);
    },

    addCommentToRemark(comment) {
      const remark = this.findRemark(comment.commentable_id);
      if (!remark) return;

      const comments = remark.comments || [];
      const index = comments.findIndex(
        (item) => String(item.id) === String(comment.id)
      );
      if (index === -1) {
        comments.push(comment);
      } else {
        comments.splice(index, 1, comment);
      }

      this.upsertRemark({ ...remark, comments }, false);
    },

    removeCommentFromRemark(comment) {
      const remark = comment.commentable_id
        ? this.findRemark(comment.commentable_id)
        : this.remarks.find((item) => (item.comments || []).some(
          (existingComment) => String(existingComment.id) === String(comment.id)
        ));
      if (!remark) return;

      const comments = (remark.comments || []).filter(
        (item) => String(item.id) !== String(comment.id)
      );

      this.upsertRemark({ ...remark, comments }, false);
    },

    findRemark(id) {
      return this.remarks.find(
        (remark) => String(remark.id) === String(id)
      ) || (String(this.selectedRecord?.id) === String(id) ? this.selectedRecord : null);
    },

    remarkParams(params = {}) {
      const normalizedParams = {
        ...params,
      };
      normalizedParams["q[archived]"] =
        normalizedParams["q[archived]"] || "active";

      return Object.fromEntries(
        Object.entries(normalizedParams).filter(([, value]) =>
          value !== undefined && value !== null && value !== ""
        )
      );
    },
  },
});
